import { useState, useEffect } from 'react';
import { Lightbulb, CheckCircle2, X, Loader2, Briefcase, Award } from 'lucide-react';
import { usePortfolio } from '@contexts/PortfolioContext';
import portfolioService from '@services/portfolio.service';
import ProfessionalOpportunities from './ProfessionalOpportunities';

const Opportunity = () => {
  const {
    careerStage,
    opportunityType,
    updateOpportunityType,
    selectedIndustryOutlook,
    jobSector,
    selectedJobs,
    toggleJob,
    updateSelectedJobs,
    selectedExams,
    toggleExam,
    updateSelectedExams,
  } = usePortfolio();

  // If professional, show the professional opportunities component
  if (careerStage === 'professional') {
    return <ProfessionalOpportunities />;
  }

  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState(null);

  const [jobExams, setJobExams] = useState([]);
  const [jobExamsLoading, setJobExamsLoading] = useState(false);
  const [jobExamsError, setJobExamsError] = useState(null);

  const selectedJobIds = selectedJobs.map((j) => j.id);
  const selectedExamIds = selectedExams.map((e) => e.id);

  useEffect(() => {
    const fetchJobs = async () => {
      if (careerStage !== 'fresher' || !jobSector || selectedIndustryOutlook.length === 0) {
        setJobs([]);
        return;
      }

      try {
        setJobsLoading(true);
        setJobsError(null);

        const allJobs = [];
        
        for (const industry of selectedIndustryOutlook) {
          const params = {
            sector: jobSector,
          };

          if (jobSector === 'Private') {
            params.industry = industry;
          } else if (jobSector === 'Government') {
            params.category = industry;
          }

          const response = await portfolioService.getJobs(params);
          const jobsList = response?.data?.data || [];
          allJobs.push(...jobsList);
        }

        setJobs(allJobs);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setJobsError('Failed to load jobs. Please try again.');
        setJobs([]);
      } finally {
        setJobsLoading(false);
      }
    };

    fetchJobs();
  }, [careerStage, jobSector, selectedIndustryOutlook]);

  useEffect(() => {
    const fetchJobExams = async () => {
      // Only fetch exams for fresher with Private sector (not Government)
      if (careerStage !== 'fresher' || !jobSector || jobSector === 'Government') {
        setJobExams([]);
        return;
      }

      try {
        setJobExamsLoading(true);
        setJobExamsError(null);
        const response = await portfolioService.getExams({
          userStage: 'fresher',
          examPurposeType: 'job',
          groupByCategory: true,
        });

        const groups = response?.data?.data || response?.data || [];
        const allExams = [];
        groups.forEach(group => {
          if (group.exams && Array.isArray(group.exams)) {
            allExams.push(...group.exams);
          }
        });
        setJobExams(allExams);
      } catch (error) {
        console.error('Error fetching job exams:', error);
        setJobExamsError('Failed to load exams. Please try again.');
        setJobExams([]);
      } finally {
        setJobExamsLoading(false);
      }
    };

    fetchJobExams();
  }, [careerStage, jobSector]);

  return (
    <div className="space-y-3 sm:space-y-4 max-w-full overflow-hidden">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 max-w-full overflow-hidden">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-[#fee2e2] rounded-lg flex-shrink-0">
            <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-[#c33c3d]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight truncate">
              Opportunities
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              Discover internships, jobs, and career opportunities
            </p>
          </div>
        </div>
      </div>

      {/* Student: Government / Private opportunity selector (unchanged) */}
      {careerStage !== 'fresher' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 lg:p-5 max-w-full overflow-hidden">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
            Opportunity Type
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
            Select between Government and Private sector opportunities
          </p>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <button
              onClick={() => updateOpportunityType('govt')}
              className={`p-3 sm:p-4 rounded-lg border transition-all text-center font-semibold text-sm sm:text-base ${
                opportunityType === 'govt'
                  ? 'border-secondary-500 bg-secondary-50 text-secondary-600'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              Government Sector
            </button>
            <button
              onClick={() => updateOpportunityType('private')}
              className={`p-3 sm:p-4 rounded-lg border transition-all text-center font-semibold text-sm sm:text-base ${
                opportunityType === 'private'
                  ? 'border-secondary-500 bg-secondary-50 text-secondary-600'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              Private Sector
            </button>
          </div>
        </div>
      )}

      {/* Fresher: Job Exams Listing - Only for Private sector */}
      {careerStage === 'fresher' && jobSector && jobSector !== 'Government' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 lg:p-5 max-w-full overflow-hidden space-y-4">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#fee2e2] flex items-center justify-center flex-shrink-0">
              <Award className="w-4 h-4 sm:w-5 sm:h-5 text-[#c33c3d]" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm sm:text-base font-bold text-gray-900">
                Recommended Exams for {jobSector} Sector Jobs
              </h3>
              <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                Select exams to improve your job prospects
              </p>
            </div>
          </div>

          {jobExamsLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 text-[#c33c3d] animate-spin mr-2" />
              <span className="text-xs sm:text-sm text-gray-600">Loading exams...</span>
            </div>
          )}

          {!jobExamsLoading && jobExamsError && (
            <div className="text-xs sm:text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {jobExamsError}
            </div>
          )}

          {!jobExamsLoading && !jobExamsError && jobExams.length === 0 && (
            <div className="text-xs sm:text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg px-3 py-6 text-center">
              No exams available for this sector.
            </div>
          )}

          {!jobExamsLoading && !jobExamsError && jobExams.length > 0 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {jobExams.map((exam) => {
                  const isSelected = selectedExamIds.includes(exam.id);
                  return (
                    <button
                      key={exam.id}
                      type="button"
                      onClick={() => toggleExam(exam)}
                      className={`relative text-left rounded-xl border p-3 sm:p-4 pr-9 sm:pr-10 transition-all duration-200 ${
                        isSelected
                          ? 'border-red-500 bg-red-50/60 shadow-md ring-2 ring-red-200'
                          : 'border-gray-200 bg-white hover:border-red-300 hover:shadow-sm'
                      }`}
                    >
                      <div
                        className={`absolute top-3 right-3 sm:top-3.5 sm:right-3.5 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all ${
                          isSelected
                            ? 'bg-red-500 border border-red-500 shadow-sm'
                            : 'bg-white border border-gray-300'
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && (
                          <svg
                            className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.704 5.296a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414L8.5 12.086l6.79-6.79a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>

                      {isSelected && (
                        <span className="inline-flex items-center gap-1 mb-2 px-2 py-0.5 rounded-full bg-red-500 text-white text-[10px] sm:text-[11px] font-semibold">
                          <CheckCircle2 className="w-3 h-3" />
                          Selected
                        </span>
                      )}

                      <h4 className="text-sm sm:text-base font-bold text-gray-900 leading-snug">
                        {exam.examName}
                        {exam.fullForm && exam.fullForm !== exam.examName && (
                          <span className="font-bold"> ({exam.fullForm})</span>
                        )}
                      </h4>

                      <p className="text-xs sm:text-sm text-red-500 font-medium mt-1">
                        Category: {exam.category}
                      </p>

                      <div className="mt-3">
                        <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-1.5">
                          Purpose:
                        </p>
                        <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">
                          {exam.purpose}
                        </p>
                      </div>

                      <div className="mt-3">
                        <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-1.5">
                          Eligibility:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {exam.eligibility
                            .split(/[+,]/)
                            .map((s) => s.trim())
                            .filter(Boolean)
                            .map((tag, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded-md bg-red-50 text-red-600 text-[10px] sm:text-[11px] font-medium"
                              >
                                {tag}
                              </span>
                            ))}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedExams.length > 0 && (
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-semibold text-gray-800">
                        Selected Exams ({selectedExams.length})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateSelectedExams([])}
                      className="text-[11px] sm:text-xs font-semibold text-red-600 hover:text-red-700"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedExams.map((exam) => (
                      <span
                        key={exam.id}
                        className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-red-50 border border-red-200 rounded-full text-[11px] sm:text-xs text-red-700 font-medium"
                      >
                        {exam.examName}
                        <button
                          type="button"
                          onClick={() => toggleExam(exam)}
                          className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600"
                          aria-label={`Remove ${exam.examName}`}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Fresher: Jobs Listing */}
      {careerStage === 'fresher' && jobSector && selectedIndustryOutlook.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 lg:p-5 max-w-full overflow-hidden space-y-4">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm sm:text-base font-bold text-gray-900">
                Available Jobs in {jobSector} Sector
              </h3>
              <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                Showing jobs based on your selected industries: {selectedIndustryOutlook.join(', ')}
              </p>
            </div>
          </div>

          {jobsLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 text-primary-500 animate-spin mr-2" />
              <span className="text-xs sm:text-sm text-gray-600">Loading jobs...</span>
            </div>
          )}

          {!jobsLoading && jobsError && (
            <div className="text-xs sm:text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {jobsError}
            </div>
          )}

          {!jobsLoading && !jobsError && jobs.length === 0 && (
            <div className="text-xs sm:text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg px-3 py-6 text-center">
              No jobs available for the selected industries.
            </div>
          )}

          {!jobsLoading && !jobsError && jobs.length > 0 && (
            <>
              <div className="mb-3">
                <p className="text-xs sm:text-sm font-semibold text-gray-700">
                  Found {jobs.length} job{jobs.length !== 1 ? 's' : ''} matching your preferences
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {jobs.map((job) => {
                  const isSelected = selectedJobIds.includes(job.id);
                  return (
                    <button
                      key={job.id}
                      type="button"
                      onClick={() => toggleJob(job)}
                      className={`relative text-left rounded-xl border p-3 sm:p-4 pr-9 sm:pr-10 transition-all duration-200 ${
                        isSelected
                          ? 'border-[#c33c3d] bg-[#fee2e2]/60 shadow-md ring-2 ring-[#fecaca]'
                          : 'border-gray-200 bg-white hover:border-[#c33c3d] hover:shadow-sm'
                      }`}
                    >
                      <div
                        className={`absolute top-3 right-3 sm:top-3.5 sm:right-3.5 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all ${
                          isSelected
                            ? 'bg-[#c33c3d] border border-[#c33c3d] shadow-sm'
                            : 'bg-white border border-gray-300'
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && (
                          <svg
                            className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.704 5.296a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414L8.5 12.086l6.79-6.79a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>

                      {isSelected && (
                        <span className="inline-flex items-center gap-1 mb-2 px-2 py-0.5 rounded-full bg-[#c33c3d] text-white text-[10px] sm:text-[11px] font-semibold">
                          <CheckCircle2 className="w-3 h-3" />
                          Selected
                        </span>
                      )}

                      <h4 className="text-sm sm:text-base font-bold text-gray-900 leading-snug">
                        {job.title}
                      </h4>

                      <p className="text-xs sm:text-sm text-[#c33c3d] font-medium mt-1">
                        {job.sector === 'Government'
                          ? `Category: ${job.category || ''}`
                          : `Industry: ${job.industry || ''}`}
                      </p>

                      {job.dept_org && (
                        <div className="mt-3">
                          <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-1.5">
                            {job.sector === 'Government' ? 'Department:' : 'Organization:'}
                          </p>
                          <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">
                            {job.dept_org}
                          </p>
                        </div>
                      )}

                      {job.skills && job.skills.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-1.5">
                            Required Skills:
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {job.skills.map((skill, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#fee2e2] text-[#c33c3d] text-[10px] sm:text-[11px] font-medium"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {selectedJobs.length > 0 && (
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 className="w-4 h-4 text-[#c33c3d] flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-semibold text-gray-800">
                        Selected Jobs ({selectedJobs.length})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateSelectedJobs([])}
                      className="text-[11px] sm:text-xs font-semibold text-[#c33c3d] hover:text-[#b02f30]"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedJobs.map((job) => (
                      <span
                        key={job.id}
                        className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-[#fee2e2] border border-[#fecaca] rounded-full text-[11px] sm:text-xs text-[#c33c3d] font-medium"
                      >
                        {job.title}
                        <button
                          type="button"
                          onClick={() => toggleJob(job)}
                          className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-[#fee2e2] hover:bg-[#fecaca] text-[#c33c3d]"
                          aria-label={`Remove ${job.title}`}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Opportunity;
